import {
  computed, ref, h, reactive, onMounted,
} from 'vue';
import useFacets from './useFacets';
import CriteriaWrapper from '../components/criteria/CriteriaWrapper';
import useSortFilter from './useSortFilter';
import useOneClickSaleFilter from './useOneClickSaleFilter';

const activeDropdown = ref(null);

export default function useFacetOptions() {
  const {
    allSelectedFacets,
    isSlideoutMode,
    facets,
    isDesktop,
    isMobile,
    globalOptions,
    isMobileInLandscape,
    openedDropdown,
  } = useFacets();

  const { isSYS, isDynamicFilters } = globalOptions;

  const {
    allSingleSelectedFacets,
    rightSideDrawer,
  } = useSortFilter();

  const { deviceInfo } = globalOptions;

  const { oneClickSaleFilterEnabled, buildOneClickSaleFilter } = useOneClickSaleFilter();

  const isReady = ref(false);

  onMounted(() => {
    isReady.value = true;
  });

  const isNotMobile = computed(() => !isMobileInLandscape.value && !isMobile.value);
  const isDesktopExperiment = computed(() => isNotMobile.value || isDesktop.value);

  const selectedFacetCount = (facet) => [].concat(...facet.selectedValues.map((obj) => obj?.selectedValues)).length;

  const getFacetInUse = (facet) => {
    // TODO remove allSingleSelectedFacets once scaled
    if (!isDynamicFilters.value && allSingleSelectedFacets?.value) {
      /* istanbul ignore else */
      if (allSingleSelectedFacets?.value?.displayName === facet.displayName) {
        return allSingleSelectedFacets.value;
      }
    }
    return facet; // store facet
  };

  const getFacetOptions = (facet, isOpened) => {
    let options = {};

    switch (facet.displayName) {
    case 'BRAND':
      options = isOpened ? { keepSearchValue: true, shouldRestoreScroll: isOpened } : {};
      break;
    case 'COLOR_NORMAL':
      options = { renderDuringSsr: true };
      break;
    default:
      options = { shouldRestoreScroll: isOpened };
      break;
    }

    if (isDynamicFilters.value) {
      options.isDynamic = true;
    }

    return options;
  };

  const renderDropDown = (facet, dropdown) => {
    const options = getFacetOptions(facet);
    /* istanbul ignore else */
    if (isSlideoutMode.value && !facet.alwaysDropdown) {
      /* istanbul ignore else */
      if (isDesktopExperiment.value) {
        // render only on client side and do not show the dropdown when slideout
        const isDesktopServer = !isReady.value && deviceInfo.value.isDesktop;
        return (isDesktopServer || rightSideDrawer.value) && !options?.renderDuringSsr ? {} : dropdown;
      }
      return undefined; // replicate prev behaviour
    }
    return dropdown; // replicate prev behaviour
  };

  const setIsOpened = (facet) => openedDropdown.value?.id === `facet_${facet.displayName}` && openedDropdown.value?.dropdown?.isOpened;

  const buildOption = (facet) => {
    const number = isDynamicFilters.value ? '' : (selectedFacetCount(facet) || '');
    const dropdownOptions = isNotMobile.value ? { isOpened: setIsOpened(facet) } : {};

    return {
      id: `facet_${facet.displayName}`,
      contents: { text: facet.name, number },
      outlined: facet.selectedValues.length > 0,
      dropdown: renderDropDown(facet, {
        customComponent: h(CriteriaWrapper, {
          facet: getFacetInUse(facet),
          facetOptions: getFacetOptions(facet, dropdownOptions.isOpened),
        }),
        ...dropdownOptions,
      }),
      isSelected: facet?.selectedValues?.length > 0,
      alwaysDropdown: isDesktopExperiment.value || facet.alwaysDropdown, // for hf options
      selectionOrder: facet.selectionOrder,
      tagVal: facet.displayName,
    };
  };

  const reduceTotalCount = (accumulator, facet) => {
    const count = selectedFacetCount(facet);
    const total = accumulator + count;
    return total;
  };

  const buildUtilityOption = (number, filterLabel, showNumber = true) => reactive({
    id: 'filter',
    contents: {
      icon: 'filter-dark-medium',
      text: filterLabel,
      number: showNumber ? number : null,
    },
    outlined: number > 0,
  });

  const reduceSortOptions = (sorted, facet) => {
    sorted[(facet.isSelected === undefined) ? 0 : 1].push(facet);
    return sorted;
  };

  const sortOptions = (options) => {
    const optionsSorted = options.reduce(reduceSortOptions, [[], []]);

    optionsSorted[0].sort((a, b) => a.selectionOrder - b.selectionOrder);
    return optionsSorted[0].concat(optionsSorted[1]);
  };

  const calculateMaxFilters = () => {
    /* istanbul ignore next */
    // xapi device types
    const { isDesktop: isXapiDesktop, isTablet: isXapiTablet, isPhone: isXapiPhone } = globalOptions.deviceInfo.value;
    const deviceCheck = isXapiTablet || isXapiPhone;

    const maxDesktopFilters = globalOptions?.maxDesktopFilters?.value;
    const maxTabletFiltersNumber = globalOptions?.maxTabletFiltersNumber?.value;

    const numberOfFilters = (isXapiDesktop && maxDesktopFilters) || (deviceCheck && maxTabletFiltersNumber) || 0;
    return oneClickSaleFilterEnabled.value ? numberOfFilters + 1 : numberOfFilters;
  };

  function removeFacetsByDisplayName(facetsList, displayNames) {
    return facetsList?.filter((facet) => !displayNames[facet.displayName]);
  }

  const facetsOptions = computed(() => {
    // facets that we do not want to show in horizontal facets
    const displayNamesToRemove = {
      DELIVERY_AND_PICKUP: true,
      SPECIAL_OFFERS: oneClickSaleFilterEnabled.value,
    };
    const storeFacets = removeFacetsByDisplayName(facets.value, displayNamesToRemove) || [];
    let options = storeFacets.map(buildOption);

    if (isSlideoutMode.value) {
      // add filter facet in the beginning
      const totalCount = allSelectedFacets.value.reduce(reduceTotalCount, 0);
      const updatedCount = isSYS.value && totalCount > 0 ? totalCount - 1 : totalCount;
      const filterLabel = ' ';

      const optionsToAdd = [buildUtilityOption(updatedCount, filterLabel, !isDynamicFilters.value)];
      if (oneClickSaleFilterEnabled.value) {
        optionsToAdd.push(buildOneClickSaleFilter());
      }

      options.unshift(...optionsToAdd);

      /* istanbul ignore else */
      if (globalOptions.pageType.value !== 'registry') {
        // if we are not in Registry!
        options = sortOptions(options);

        /* istanbul ignore next */
        const maxFilters = calculateMaxFilters();
        /* istanbul ignore else */
        if (maxFilters > 0) {
          options = options.slice(0, maxFilters);
        }
      }
    }
    return options;
  });

  const focusActiveDropdown = () => {
    /* istanbul ignore else */
    if (activeDropdown.value) {
      activeDropdown.value.focus();
    }
  };

  const setActiveDropdown = (dropdown) => {
    activeDropdown.value = dropdown;
  };

  return {
    focusActiveDropdown,
    setActiveDropdown,
    facetsOptions,
    activeDropdown,
    renderDropDown,
    buildOption,
  };
}
